'use client';

import { useTranslations } from 'next-intl';
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { useSession } from 'next-auth/react';

import { Link } from '@/i18n/routing';
import type { ICartItem } from '@/interfaces/cart.interface';

import { getSessionIdForCart } from '@/services/StorageService';
import { addToCart, getCart, removeFromCart } from '@/services/CartService';

import { StoreContext } from '../Store';
import Title from '../Title';
import Spinner from '../ui/Spinner';
import CartItem from './CartItem';

import { useTaost } from '@/hooks/useToast';

type ShoppingCartModalProps = {
  onClose: () => void;
};

const ShoppingCartModal: FC<ShoppingCartModalProps> = ({ onClose }) => {
  const { cartState } = useContext(StoreContext);
  const [carts, setCarts] = cartState;
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const t = useTranslations('shopping_cart_modal');
  const { errorToast } = useTaost();
  const { data: session } = useSession();
  const user = session?.user;
  const sessionId = !user ? getSessionIdForCart() : null;

  const fetchCartData = async () => {
    try {
      const data = await getCart(sessionId);
      setLoading(true);
      setCarts(data?.data);
    } catch (error) {
      console.error('Error fetching cart data: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (item: ICartItem) => {
    const itemId = `${item.product._id}-${item.variant.id}-${item.size.id}`;
    setIsUpdating(prev => ({ ...prev, [itemId]: true }));

    try {
      await removeFromCart({
        sessionId,
        product: item.product._id,
        variant: item.variant.id,
        size: item.size.id,
        quantity: item.quantity,
      });
      await fetchCartData();
    } catch (error) {
      console.error('Failed to remove item from cart:', error);
    } finally {
      setIsUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleQuantityChange = async (item: ICartItem, newQuantity: number) => {
    const itemId = `${item.product._id}-${item.variant.id}-${item.size.id}`;
    if (isUpdating[itemId]) return;
    setIsUpdating(prev => ({ ...prev, [itemId]: true }));

    try {
      if (newQuantity < 1) {
        await handleRemoveItem(item);
      } else {
        const currentQuantity =
          carts?.cartItems?.find(
            i => i.product._id === item.product._id && i.variant.id === item.variant.id && i.size.id === item.size.id,
          )?.quantity ?? 0;
        const diff = newQuantity - currentQuantity;
        if (diff !== 0) {
          await addToCart({
            sessionId,
            product: item.product._id,
            variant: item.variant.id,
            size: item.size.id,
            quantity: diff,
          });
        }
        await fetchCartData();
      }
    } catch (error: any) {
      errorToast(error?.response?.data?.message);
      console.error('Failed to update item quantity:', error);
    } finally {
      setIsUpdating(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const subtotal = useMemo(() => {
    return carts?.cartItems?.reduce((total, item) => total + item.variant.price * item.quantity, 0) || 0;
  }, [carts]);

  useEffect(() => {
    fetchCartData();
    setIsVisible(true);
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    document.body.style.overflow = '';
    setTimeout(onClose, 400);
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex h-screen justify-end bg-black bg-opacity-50 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
    >
      <div
        className={`ease-[cubic-bezier(0.25, 1, 0.5, 1)] flex h-full w-full max-w-md transform flex-col bg-white shadow-lg transition-all duration-500 dark:border-l dark:border-clr-2e dark:bg-black dark:bg-gradient-to-l dark:from-[#121212] dark:via-[#22242bc9] ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6">
          <Title title={t('shopping_cart_modal')} />
          <button onClick={handleClose} className="text-2xl font-bold text-clr-6A hover:text-gray-800 dark:text-clr-a4">
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {loading ? (
            <Spinner />
          ) : (
            <>
              <p className="mb-4 text-base text-clr-0a dark:text-white">
                {t('you_have')}{' '}
                <span className="font-bold">
                  {carts?.cartItems?.length ? carts?.cartItems?.length : 0} {t('items')}
                </span>{' '}
                {t('your_cart')}
              </p>
              {carts?.cartItems?.map((item, index) => {
                const itemId = `${item.product._id}-${item.variant.id}-${item.size.id}`;
                return (
                  <React.Fragment key={index}>
                    <CartItem
                      key={itemId}
                      item={item}
                      onRemove={() => handleRemoveItem(item)}
                      onQuantityChange={newQuantity => handleQuantityChange(item, newQuantity)}
                      isUpdating={!!isUpdating[itemId]}
                    />
                  </React.Fragment>
                );
              })}
            </>
          )}

          <Link
            href="/cart"
            className={`mt-4 block text-center text-sm font-medium text-clr-20 hover:underline ${carts?.cartItems.length === 0 ? 'hidden' : ''}`}
            onClick={handleClose}
          >
            {t('shopping_cart_view_cart')}
          </Link>
        </div>

        <div className="border-t p-6">
          <div className="mb-4 flex justify-between text-base font-semibold">
            <span>{t('shopping_cart_subtotal')}</span>
            <span className="font-bold">€{subtotal}</span>
          </div>
          <div>
            <Link
              href={'/checkout'}
              className="flex w-full max-w-[446px] items-center justify-center border border-black bg-clr-20 px-8 py-3 text-base text-white transition-all duration-300 hover:bg-transparent hover:text-black dark:hover:border-white dark:hover:text-white"
              onClick={handleClose}
            >
              {t('shopping_cart_proccess')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingCartModal;
